import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
  List,
  ListItem,
  Avatar,
  Chip,
  Tooltip,
  Typography,
  Divider,
} from '@material-ui/core';
import CallSplitIcon from '@material-ui/icons/CallSplit';
import { Link } from '@backstage/core-components';
import { PullRequest, RepoResult } from '../types';
import {
  CLAUDE_SCORE_MAX,
  parseLabel,
  totalScoreColor,
  dimensionColor,
} from './labelUtils';

const useStyles = makeStyles(theme => ({
  listItem: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    padding: theme.spacing(0.75, 1),
  },
  topRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
    width: '100%',
    flexWrap: 'wrap',
  },
  prNumber: {
    color: theme.palette.text.secondary,
    fontSize: '0.75rem',
    whiteSpace: 'nowrap',
  },
  title: {
    fontSize: '0.8rem',
    flex: 1,
    minWidth: 0,
  },
  chip: {
    height: 18,
    fontSize: '0.65rem',
  },
  chipGreen: {
    backgroundColor: '#2da44e',
    color: '#fff',
  },
  chipYellow: {
    backgroundColor: '#d4a017',
    color: '#fff',
  },
  chipRed: {
    backgroundColor: '#cf222e',
    color: '#fff',
  },
  chipGrey: {
    backgroundColor: theme.palette.grey[400],
    color: '#fff',
  },
  bottomRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    marginTop: theme.spacing(0.25),
    flexWrap: 'wrap',
  },
  avatar: {
    width: 20,
    height: 20,
  },
  authorName: {
    fontSize: '0.72rem',
    color: theme.palette.text.secondary,
  },
  age: {
    fontSize: '0.72rem',
    color: theme.palette.text.secondary,
  },
  labelChip: {
    height: 16,
    fontSize: '0.6rem',
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
    padding: theme.spacing(0.75, 1),
    backgroundColor: theme.palette.background.default,
  },
  sectionIcon: {
    fontSize: '1rem',
    color: theme.palette.text.secondary,
  },
  sectionTitle: {
    fontSize: '0.8rem',
    fontWeight: 600,
  },
  sectionCount: {
    fontSize: '0.72rem',
    color: theme.palette.text.secondary,
  },
  errorText: {
    padding: theme.spacing(1),
    color: theme.palette.error.main,
    fontSize: '0.8rem',
  },
  emptyText: {
    padding: theme.spacing(1),
    color: theme.palette.text.secondary,
    fontSize: '0.8rem',
  },
}));

export function relativeAge(createdAt: string): string {
  const now = Date.now();
  const created = new Date(createdAt).getTime();
  const diffMs = now - created;
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return 'today';
  if (diffDays === 1) return '1 day ago';
  if (diffDays < 30) return `${diffDays} days ago`;
  const diffMonths = Math.floor(diffDays / 30);
  if (diffMonths === 1) return '1 month ago';
  if (diffMonths < 12) return `${diffMonths} months ago`;
  const diffYears = Math.floor(diffMonths / 12);
  return diffYears === 1 ? '1 year ago' : `${diffYears} years ago`;
}

export function scoreChipClass(
  score: string | null,
  classes: ReturnType<typeof useStyles>,
): string {
  const color = totalScoreColor(score);
  if (color === 'green') return classes.chipGreen;
  if (color === 'yellow') return classes.chipYellow;
  if (color === 'red') return classes.chipRed;
  return classes.chipGrey;
}

export function dimensionChipClass(
  value: number,
  classes: ReturnType<typeof useStyles>,
): string {
  const color = dimensionColor(value);
  if (color === 'green') return classes.chipGreen;
  if (color === 'yellow') return classes.chipYellow;
  return classes.chipRed;
}

export function repoName(slug: string): string {
  const parts = slug.split('/');
  return parts[parts.length - 1] ?? slug;
}

export function PRList({ pullRequests }: { pullRequests: PullRequest[] }) {
  const classes = useStyles();

  if (pullRequests.length === 0) {
    return (
      <Typography className={classes.emptyText}>No open pull requests.</Typography>
    );
  }

  return (
    <List disablePadding>
      {pullRequests.map((pr, idx) => (
        <React.Fragment key={pr.number}>
          {idx > 0 && <Divider />}
          <ListItem className={classes.listItem} disableGutters>
            <div className={classes.topRow}>
              <span className={classes.prNumber}>#{pr.number}</span>
              <Link to={pr.url} className={classes.title}>
                {pr.title}
              </Link>
              {pr.draft && (
                <Chip label="Draft" size="small" className={classes.chip} />
              )}
              <Tooltip title="Claude review score">
                <Chip
                  label={pr.claudeScore !== null ? `${pr.claudeScore}/${CLAUDE_SCORE_MAX}` : '—'}
                  size="small"
                  className={`${classes.chip} ${scoreChipClass(pr.claudeScore, classes)}`}
                />
              </Tooltip>
            </div>
            <div className={classes.bottomRow}>
              <Avatar
                src={pr.authorAvatar}
                alt={pr.author}
                className={classes.avatar}
              />
              <span className={classes.authorName}>{pr.author}</span>
              <span className={classes.age}>{relativeAge(pr.createdAt)}</span>
              {[...pr.labels].sort().map(label => {
                const parsed = parseLabel(label);
                if (parsed.kind === 'score') return null;
                if (parsed.kind === 'dimension') {
                  return (
                    <Chip
                      key={label}
                      label={parsed.display}
                      size="small"
                      className={`${classes.labelChip} ${dimensionChipClass(parsed.value, classes)}`}
                    />
                  );
                }
                return (
                  <Chip
                    key={label}
                    label={parsed.text}
                    size="small"
                    className={classes.labelChip}
                  />
                );
              })}
            </div>
          </ListItem>
        </React.Fragment>
      ))}
    </List>
  );
}

export function RepoSection({ result }: { result: RepoResult }) {
  const classes = useStyles();
  const name = repoName(result.repo);
  const count = result.pullRequests?.length ?? 0;

  return (
    <div>
      <div className={classes.sectionHeader}>
        <CallSplitIcon className={classes.sectionIcon} />
        <span className={classes.sectionTitle}>{name}</span>
        {!result.error && (
          <span className={classes.sectionCount}>
            {count} open {count === 1 ? 'PR' : 'PRs'}
          </span>
        )}
      </div>
      {result.error ? (
        <Typography className={classes.errorText}>{result.error}</Typography>
      ) : (
        <PRList pullRequests={result.pullRequests} />
      )}
    </div>
  );
}
